import { ArrowRight, Loader2, Megaphone } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  getFinanceMetaConnection,
  getFinanceMetaOAuthUrl,
} from "../../services/financeService";
import { openMetaOAuthPopup } from "../../services/metaOAuthPopup";
import { FinanceOnboardingCta } from "./FinanceOnboardingCta";

interface StepProps {
  connected: boolean;
  onConnectionChange: (connected: boolean) => void;
  onNext: () => void;
}

const isMetaConnected = (connection: Awaited<ReturnType<typeof getFinanceMetaConnection>>) => {
  return connection.connected === true;
};

export const Step2ConnectMeta = ({
  connected,
  onConnectionChange,
  onNext,
}: StepProps) => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const syncMetaIntegration = useCallback(async () => {
    try {
      const connection = await getFinanceMetaConnection();
      const active = isMetaConnected(connection);
      onConnectionChange(active);
      return active;
    } catch {
      onConnectionChange(false);
      return false;
    }
  }, [onConnectionChange]);

  useEffect(() => {
    let mounted = true;

    const loadIntegration = async () => {
      setLoading(true);
      await syncMetaIntegration();
      if (mounted) setLoading(false);
    };

    loadIntegration();

    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [syncMetaIntegration]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "fluxi:meta-oauth") return;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      await syncMetaIntegration();
      setConnecting(false);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [syncMetaIntegration]);

  const handleConnect = async () => {
    if (connected || connecting) return;

    setConnecting(true);
    try {
      const response = await getFinanceMetaOAuthUrl();
      if (!response.authorizationUrl) {
        toast.error("No pudimos iniciar la conexión con Meta Ads.");
        setConnecting(false);
        return;
      }
      const popup = openMetaOAuthPopup(
        response.authorizationUrl,
        "meta-oauth",
        { width: 620, height: 760 }
      );

      if (!popup) {
        window.location.href = response.authorizationUrl;
        return;
      }

      intervalRef.current = setInterval(async () => {
        if (popup.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          await syncMetaIntegration();
          setConnecting(false);
        }
      }, 500);
    } catch {
      toast.error("No pudimos iniciar la conexión con Meta Ads.");
      setConnecting(false);
    }
  };

  const isBusy = loading || connecting;

  return (
    <div className="w-full max-w-2xl bg-white shadow-xl rounded-[50px] p-8 md:p-12">
      <div className="mx-auto max-w-lg text-center">
        <h1 className="text-4xl font-bold mb-3">
          <span className="text-gray-900">Conecta </span>
          <span className="text-[#1877F2]">Meta Ads</span>
        </h1>
        <p className="text-base font-medium text-[#64748B] leading-7">
          Sabrás qué campañas valen la pena escalar y cuáles apagar.
        </p>
      </div>

      {connected ? (
        <>
          <div className="mx-auto mt-8 w-full max-w-md rounded-[24px] border border-gray-100 bg-white px-6 py-5 shadow-[0_8px_30px_rgba(67,104,229,0.08)]">
              <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border border-blue-100 bg-blue-50 text-[#1877F2]">
                <Megaphone className="h-7 w-7" />
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-gray-900">Meta Ads</h2>
                <p className="mt-1 text-sm font-medium text-[#64748B]">Facebook · Instagram</p>
              </div>

              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-1 text-[11px] font-bold text-[#059669]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" />
                Conectado
              </span>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <FinanceOnboardingCta onClick={onNext}>
              Continuar <ArrowRight className="h-5 w-5" />
            </FinanceOnboardingCta>
          </div>
        </>
      ) : (
        <div className="mt-8 flex justify-center">
          <FinanceOnboardingCta onClick={handleConnect} disabled={isBusy}>
            {isBusy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Megaphone className="h-5 w-5" />
            )}
            Conectar Meta Ads
          </FinanceOnboardingCta>
        </div>
      )}
    </div>
  );
};
